import { useCallback, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ErrorMessage, Field } from '@atlaskit/form';
import UserPicker, { isValidEmail, type OnChange, type Value } from '@atlaskit/user-picker';

import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { ANALYTICS_PREFIX } from '../../../common/constants.tsx';
import {
	EmailInviteValidationError,
	MAX_ALLOWED_EMAILS,
} from '../hooks/use-email-invites-input.types.ts';
import { extractEmailDomain } from '../utils.ts';

import messages from './messages.tsx';

import type { UseEmailInvitesInput } from '../hooks/use-email-invites-input.types.ts';

const FIELD_NAME = 'onboarding-wizard-invite-emails';

type EmailInputComponentProps = {
	validatedEmails: UseEmailInvitesInput['validatedEmails'];
	addEmail: UseEmailInvitesInput['addEmail'];
	removeEmail: UseEmailInvitesInput['removeEmail'];
};

const asEmailStrings = (value: Value): string[] => {
	if (!value) {
		return [];
	}
	const options = Array.isArray(value) ? value : [value];
	return options.filter((option) => option.type === 'email').map((option) => option.id);
};

/**
 * In-house email input for the onboarding-wizard invite step. Renders the picker, maps add
 * results to inline errors, and derives the domain suggestion. Email list + validation come
 * from useEmailInvitesInput (owned by the parent) via the validatedEmails/addEmail/removeEmail
 * props.
 *
 * Do NOT use outside of fg: spice-refactor-jira-onboarding-wizard-invites, contact #help-spice for details
 */
export const EmailInputComponent = ({
	validatedEmails,
	addEmail,
	removeEmail,
}: EmailInputComponentProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { email } = useLoggedInUser();
	const emailDomain = extractEmailDomain(email ?? '');

	const [errorState, setErrorState] = useState<EmailInviteValidationError | null>(null);
	const [inputValue, setInputValue] = useState('');
	// Only suggest "<input>@<domain>" while user hasn't specified a domain or entered invalid characters
	const shouldSuggestDomain = emailDomain !== '' && inputValue !== '' && !inputValue.includes('@');

	const handleInputChange = useCallback((value?: string) => {
		setInputValue(value ?? '');
	}, []);

	const handleFocus = useCallback(() => {
		createAnalyticsEvent({
			action: 'focused',
			actionSubject: 'component',
			actionSubjectId: `${ANALYTICS_PREFIX}InvitePeopleFocus`,
			eventType: 'ui',
		}).fire();
	}, [createAnalyticsEvent]);

	const handleChange = useCallback<OnChange>(
		(value, action) => {
			const next = asEmailStrings(value);

			if (action === 'remove-value' || action === 'clear') {
				validatedEmails
					.filter(
						(email) => !next.some((candidate) => candidate.toLowerCase() === email.toLowerCase()),
					)
					.forEach((email) => removeEmail(email));
				setErrorState(null);
				return;
			}

			const candidate = next.find(
				(email) =>
					!validatedEmails.some((existing) => existing.toLowerCase() === email.toLowerCase()),
			);

			if (!candidate) {
				return;
			}

			const result = addEmail(candidate);
			setErrorState(result.ok ? null : result.error);
		},
		[addEmail, removeEmail, validatedEmails],
	);

	const getErrorMessage = (error: EmailInviteValidationError | null) => {
		switch (error) {
			case EmailInviteValidationError.MAX_ALLOWED_EXCEEDED:
				return formatMessage(messages.emailMaxExceeded, { maxAllowedEmails: MAX_ALLOWED_EMAILS });
			case EmailInviteValidationError.INVALID_FORMAT:
				return formatMessage(messages.emailInvalidFormat);
			default:
				return null;
		}
	};

	const errorMessage = getErrorMessage(errorState);

	return (
		<div css={fieldStyles}>
			<Field name={FIELD_NAME} label={formatMessage(messages.emailShare)} isRequired={false}>
				{() => (
					<>
						<UserPicker
							fieldId={FIELD_NAME}
							allowEmail
							isMulti
							suggestEmailsForDomain={shouldSuggestDomain ? emailDomain : undefined}
							noOptionsMessage={() => null}
							isValidEmail={isValidEmail}
							placeholder={formatMessage(messages.emailSharePlaceholder)}
							onChange={handleChange}
							onInputChange={handleInputChange}
							onFocus={handleFocus}
						/>
						{errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
					</>
				)}
			</Field>
		</div>
	);
};

const fieldStyles = css({
	width: '100%',
});
